/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Suspense, useState } from 'react';
import Card from '@/components/card';
import Title from '@/components/title';
import { InboundMenu } from '@/configs/menuItems';
import { getPathSegment } from '@/utils/pathUtils';
import SubMenu from '@/components/menu/sub-menu';
import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { DatePicker } from 'antd';
import Search from 'antd/es/input/Search';
import locale from 'antd/es/date-picker/locale/th_TH';
import dayjs, { Dayjs } from 'dayjs';
import {
  INotePurchaseReport,
  ISearchNotePurchaseReportReq,
} from '@/interfaces/report';
import { searchNotePurchaseReport } from '@/adapters/report';
import _ from 'lodash';
import NotePurchaseTable from './components/note-purchase-table';
import ExcelExport from './components/export-report';
import BusinessUnitMultiSelector from '@/components/selector/business-unit-multi-selector';

const _criteria: ISearchNotePurchaseReportReq = {
  pageSize: -1,
  pageStart: 1,
  startDate: dayjs().startOf('month').toDate(),
  endDate: dayjs().endOf('month').toDate(),
};

const NotePurchaseReport: React.FC = () => {
  const pathSegment = getPathSegment();
  const [criteria, setCriteria] = useState<ISearchNotePurchaseReportReq>({
    ..._criteria,
  });

  const { data } = useQuery<AxiosResponse<INotePurchaseReport[]>, Error>({
    queryKey: ['search-note-purchase-report', criteria],
    queryFn: async () => await searchNotePurchaseReport(criteria),
  });

  const onSearch = (text: string) => {
    setCriteria({
      ...criteria,
      search: text,
      pageStart: 1,
      pageSize: 10,
    });
  };

  const onRangeChange = (dates: null | (Dayjs | null)[]) => {
    if (dates) {
      setCriteria({
        ...criteria,
        pageStart: 1,
        pageSize: 10,
        startDate: dates[0]?.toDate(),
        endDate: dates[1]?.toDate(),
      });
    } else {
      setCriteria({
        ...criteria,
        pageStart: 1,
        pageSize: 10,
        startDate: null,
        endDate: null,
      });
    }
  };

  const notePurchaseList = data?.data || [];

  const noteGroups: INotePurchaseReport[][] = Object.values(
    _.groupBy(notePurchaseList, 'invNO'),
  );

  return (
    <>
      <SubMenu active={pathSegment} items={InboundMenu} />
      <Card customClass="min-h-[200px]">
        <Title title="Note Purchase Report">
          <Suspense>
            <ExcelExport
              data={noteGroups || []}
              startDate={criteria.startDate}
            />
          </Suspense>
        </Title>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col md:flex-row justify-between gap-3">
            <div className="flex flex-col md:flex-row justify-between gap-3">
              <BusinessUnitMultiSelector
                handleSelectData={function (id: number[]): void {
                  setCriteria({
                    ...criteria,
                    bu: id.join(','),
                  });
                }}
              />
              <DatePicker.RangePicker
                defaultValue={[
                  dayjs().startOf('month'),
                  dayjs().endOf('month'),
                ]}
                size="large"
                locale={locale}
                onChange={onRangeChange}
              />
              <Search
                className="md:max-w-[400px] md:min-w-[400px]"
                placeholder="ค้นหา"
                allowClear
                size="large"
                enterButton="ค้นหา"
                onSearch={onSearch}
              />
            </div>
          </div>
          {noteGroups.map((noteGroup, index) => {
            return <NotePurchaseTable key={index} items={noteGroup || []} />;
          })}
        </div>
      </Card>
    </>
  );
};

export default NotePurchaseReport;
